import type { JSX } from "react"
import * as React from "react"
import { addClassNamesToElement } from "@lexical/utils"
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical"

import { RawHtmlComponent } from "./component"

export interface RawHtmlPayload {
  html: string
  key?: NodeKey
}

type SerializedRawHtmlNode = Spread<
  {
    html: string
  },
  SerializedLexicalNode
>

function $convertRawHtmlElement(domNode: Node): null | DOMConversionOutput {
  if (domNode.nodeName === "DIV" && (domNode as HTMLElement).hasAttribute("data-raw-html")) {
    const html = (domNode as HTMLElement).innerHTML
    return { node: $createRawHtmlNode({ html }) }
  }
  return null
}

export class RawHtmlNode extends DecoratorNode<JSX.Element> {
  __html: string

  static getType(): string {
    return "raw-html"
  }

  static clone(node: RawHtmlNode): RawHtmlNode {
    return new RawHtmlNode(node.__html, node.__key)
  }

  constructor(html: string, key?: NodeKey) {
    super(key)
    this.__html = html
  }

  static importJSON(serializedNode: SerializedRawHtmlNode): RawHtmlNode {
    return $createRawHtmlNode({ html: serializedNode.html })
  }

  exportJSON(): SerializedRawHtmlNode {
    return {
      ...super.exportJSON(),
      html: this.__html,
    }
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: () => ({ conversion: $convertRawHtmlElement, priority: 0 }),
    }
  }

  exportDOM(): DOMExportOutput {
    const container = document.createElement("div")
    container.setAttribute("data-raw-html", "true")
    container.innerHTML = this.__html
    return { element: container }
  }

  getHtml(): string {
    return this.__html
  }

  setHtml(html: string): void {
    const writable = this.getWritable()
    writable.__html = html
  }

  createDOM(): HTMLElement {
    const div = document.createElement("div")
    addClassNamesToElement(div, "editor-raw-html-block")
    return div
  }

  updateDOM(): false {
    // Decorator handles updates
    return false
  }

  decorate(): JSX.Element {
    return <RawHtmlComponent html={this.__html} nodeKey={this.getKey()} />
  }
}

export function $createRawHtmlNode({ html, key }: RawHtmlPayload): RawHtmlNode {
  return $applyNodeReplacement(new RawHtmlNode(html, key))
}

export function $isRawHtmlNode(node: LexicalNode | null | undefined): node is RawHtmlNode {
  return node instanceof RawHtmlNode
}
